import { useEffect, useState } from 'react'
import { useLocation, useNavigate, useParams } from 'react-router-dom'
import { useGoBack } from '../../hooks/useGoBack'
import { formatEventPeriod } from '../../lib/eventPeriod'
import { CalendarDays, ChevronRight, ExternalLink, MapPin } from 'lucide-react'
import Header from '../../components/Header'
import ImageLightbox from '../../components/ImageLightbox'
import { useI18n } from '../../i18n/useI18n'
import { api, type StampEventInfo } from '../../services/api'
import { PrizeLines, ProgramTitle } from './eventInfoParts'

/**
 * イベント詳細ページ（公開・ログイン不要）。
 *
 * **なぜ必要か**: 告知は開催のひと月前から出るのに、スタンプラリー画面は開催期間内しか
 * 出ない。告知の飛び先を /stamp-rally にしていたため、告知を見て来た人ほど
 * 「開催中のイベントはありません」に着地していた。開催前でも読める場所を分けて置く。
 *
 * 主催者の HP がある場合は infoUrl を優先して飛ばすので、このページは
 * **HP を持たない主催者の受け皿**という位置づけ。だから作り込みすぎない。
 *
 * #1898 **イベント (親) のページ**。HOME の告知の着地点。イベント全体の情報 (期間・場所・
 * 本文・公式サイト) と企画 (スタンプラリー / AR宝探し / ミニゲーム …) の一覧だけを出し、
 * 押すと企画詳細へ進む。
 *
 * **参加方法 (スタンプを集めにいく) と参加ブースは出さない。** どちらもスタンプラリーの
 * 企画に付くもので、ここに置くと AR宝探しやミニゲームも含めた「イベントの参加方法」に
 * 見えてしまう。企画詳細に置く (入口 QR も企画詳細を開く)。
 *
 * URL の ID は親でも企画でもよい。
 */
export default function StampEventInfo() {
  const { t, locale } = useI18n()
  const navigate = useNavigate()
  // #1361 戻るは履歴を1つ戻す。告知から直接来た場合はラリー画面へ落とす。
  const goBack = useGoBack('/stamp-rally')
  const { eventId } = useParams<{ eventId: string }>()
  const { hash } = useLocation()
  const [info, setInfo] = useState<StampEventInfo | null>(null)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    if (!eventId) return
    let cancelled = false
    api.getStampEventInfo(eventId)
      .then((res) => { if (!cancelled) setInfo(res) })
      .catch(() => { if (!cancelled) setError(t('stampRally.eventInfoFailed')) })
    return () => { cancelled = true }
  }, [eventId, t])

  // #1886 日付だけだと「10月10日 – 10月10日」になり、**知りたい開催時刻が出ない**
  const period = (ev: { startsAt: string; endsAt: string }) =>
    formatEventPeriod(ev.startsAt, ev.endsAt, locale, { month: 'long' })

  // #2492 ポップアップと下部固定の枠はスタンプ帳の画面全体に出すもの。縦長のチラシや
  // 細い帯をイベント詳細の見出しの画像に使うと形が合わないので、カードの中の枠から選ぶ
  const banner = (info?.slots ?? []).find(
    (s) => s.imageUrl && (s.placement === 'top' || s.placement === 'list'),
  )

  // #1886 配布済みの `#booths` 付きリンク (「ブースはここ」と名指ししたもの) は、ブースの
  // 位置へ送る。#1898 ブースはスタンプラリーの企画に付くので、その企画詳細のブースへ。
  useEffect(() => {
    if (!info || hash !== '#booths') return
    const stampRally = info.programs.find((p) => p.kind === 'qr')
    if (stampRally) {
      navigate(`/stamp-rally/program/${encodeURIComponent(stampRally.event.id)}#booths`, { replace: true })
    }
  }, [info, hash, navigate])

  // #1886 バナーは文字が焼き込まれたデザイン。カード幅では読めないので全画面に開ける。
  const [viewingBanner, setViewingBanner] = useState(false)

  return (
    <div className="mobile-container min-h-screen bg-warm-white safe-top safe-bottom">
      <Header title={t('stampRally.eventInfoTitle')} onBack={goBack} />

      {error && (
        <p className="px-4 py-10 text-center font-body text-sm text-text-secondary">{error}</p>
      )}

      {!info && !error && (
        <div className="flex justify-center py-16">
          <div className="h-6 w-6 animate-spin rounded-full border-2 border-navy border-t-transparent" />
        </div>
      )}

      {info && (
        <div className="flex flex-col gap-4 px-4 pb-24 pt-4 animate-fade-in">
          {banner && (
            <button
              onClick={() => setViewingBanner(true)}
              className="tap-effect block w-full overflow-hidden rounded-2xl"
            >
              <img src={banner.imageUrl} alt="" className="w-full object-cover" />
            </button>
          )}
          {viewingBanner && banner && (
            <ImageLightbox src={banner.imageUrl} onClose={() => setViewingBanner(false)} />
          )}

          <div>
            <h1 className="font-display text-2xl text-navy">{info.event.name}</h1>
            {info.event.description && (
              <p className="mt-1 font-body text-sm text-text-secondary">{info.event.description}</p>
            )}
          </div>

          <div className="flex flex-col gap-2 rounded-2xl bg-white p-4 shadow-card">
            <p className="flex items-center gap-2 font-body text-sm text-text-primary">
              <CalendarDays size={16} className="shrink-0 text-navy" />
              {period(info.event)}
            </p>
            {/* #1898 会場があれば会場を出す (地域名だけでは、どこでやるのか分からない) */}
            {info.event.venue ? (
              <p className="flex items-center gap-2 font-body text-sm text-text-primary">
                <MapPin size={16} className="shrink-0 text-navy" />
                {info.event.venue}
              </p>
            ) : (
              info.region?.name && (
                <p className="flex items-center gap-2 font-body text-sm text-text-primary">
                  <MapPin size={16} className="shrink-0 text-navy" />
                  {info.region.name}
                </p>
              )
            )}
          </div>

          {/* 本文。改行をそのまま活かす（運営はプレーンテキストで書くため） */}
          {info.event.overview && (
            <p className="whitespace-pre-wrap font-body text-sm leading-relaxed text-text-primary">
              {info.event.overview}
            </p>
          )}

          {/* #1898 企画の一覧。景品と抽選は企画ごとに別物なので、ここで分けて出す。
              押すと企画詳細へ (説明・景品・参加方法・出店店舗は企画ごと) */}
          {info.programs.length > 0 && (
            <div className="flex flex-col gap-2">
              <h2 className="font-display text-base text-navy">
                {t('stampRally.eventInfoPrograms')}
              </h2>
              {info.programs.map((program) => (
                <button
                  key={program.event.id}
                  onClick={() =>
                    navigate(`/stamp-rally/program/${encodeURIComponent(program.event.id)}`)
                  }
                  className="tap-card flex items-center gap-3 rounded-2xl bg-white p-4 text-left shadow-card"
                >
                  <div className="min-w-0 flex-1">
                    <ProgramTitle program={program} />
                    {program.event.description &&
                      program.event.description !== info.event.description && (
                        <p className="mt-1 font-body text-xs text-text-secondary">
                          {program.event.description}
                        </p>
                      )}
                    <PrizeLines prize={program.prize} lottery={program.lottery} />
                  </div>
                  <ChevronRight size={16} className="shrink-0 self-center text-text-tertiary" />
                </button>
              ))}
            </div>
          )}

          {/* 主催者の HP。infoUrl がある場合はそちらが正なので、詳細ページからも出す */}
          {info.event.infoUrl && (
            <a
              href={info.event.infoUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="tap-card flex items-center justify-between rounded-2xl bg-white px-4 py-4 shadow-card"
            >
              <span className="font-body text-sm text-text-primary">
                {t('stampRally.eventInfoOfficial')}
              </span>
              <ExternalLink size={16} className="shrink-0 text-text-tertiary" />
            </a>
          )}
        </div>
      )}
    </div>
  )
}
